import { z } from 'zod';
import { configuredOr } from '@/lib/configValue';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';

/**
 * The slide timer bar: a full-width track with a coloured fill that sweeps left to right once
 * per `interval` seconds and then restarts. Port of `progressBar.vue`.
 *
 * Two shapes, because it has two callers:
 *
 * - `ProgressBar` — prop-driven, `{ interval, layout }`. This is the one SYM-492's slider
 *   renders, and it is how the legacy app used it: `moduleLoader.vue` passed
 *   `slider.interval` and `slider.layout` directly, never config `options`.
 * - `ProgressBarWidget` — the registry adapter, reading the same two values out of
 *   `options`. Registered because the legacy app registered `ProgressBar` as a widget type
 *   and a config could start using it without a code change. **No deployed config does**, so
 *   nothing here is verified against a live layout.
 */

/** `slider.layout` — the same shape `sliderLayoutSchema` produces in `config/schema.ts`. */
export type ProgressBarLayout = {
  /** Fill colour. Default `grey`. */
  color?: string;
  /** rem. Default 1. */
  height?: number;
};

export type ProgressBarProps = {
  /** Seconds for one full sweep. Default 10, matching `slider.interval`. */
  interval?: number;
  layout?: ProgressBarLayout;
};

export function ProgressBar({ interval = 10, layout }: ProgressBarProps) {
  const height = `${String(layout?.height ?? 1)}rem`;

  /*
   * Driven by a CSS animation, not by a timer (W-12).
   *
   * Legacy ticked every 10ms and pushed `progress += 1 / interval` into reactive state — 100
   * React re-renders per second per bar on hardware that also has to keep a Chart.js canvas
   * alive. The keyframes in `global.css` produce the same sweep on the compositor, with the
   * same period: `width` goes 0% -> 100% linearly over `interval` seconds, then repeats,
   * exactly as the legacy reset at `>= 100` did.
   *
   * Two consequences worth stating, both of them fixes:
   *
   * - **There is nothing to clean up.** The animation belongs to the element and dies with
   *   it. Legacy never cleared its interval, so a slider mounting the bar per slide leaked
   *   one 100Hz timer per slide, forever (W-5).
   * - **Two bars are independent.** Legacy stored its timer handle in a *module-level*
   *   variable, so a second instance overwrote the first's handle and the two fought over
   *   one timer.
   */
  const animation = `signage-progress-sweep ${String(interval)}s linear infinite`;

  return (
    // `z-index` without `position` does nothing — legacy had the same declaration and it was
    // equally inert. Preserved rather than "fixed": raising this above its siblings is a
    // stacking change nobody has asked for and no config exercises.
    <div className="progress-bar w-full" style={{ height, zIndex: 100 }}>
      <div
        className="bar"
        style={{ height, backgroundColor: configuredOr(layout?.color, 'grey'), animation }}
      />
    </div>
  );
}

const optionsSchema = z.object({
  interval: z.number().optional(),
  layout: z
    .object({
      color: z.string().optional(),
      height: z.number().optional(),
    })
    .optional(),
});

/**
 * Registry adapter. Reads `options.interval` and `options.layout` and renders the
 * prop-driven bar above.
 *
 * Note that `options.layout` here is **not** `widgetLayoutSchema` — a `ProgressBar` has no
 * widget container, and its `layout` is the `{ color, height }` pair from `slider.layout`.
 * That collision is why this adapter exists rather than the widget reading `WidgetProps`
 * directly.
 */
export function ProgressBarWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);

  if (!parsed.success) {
    warnOnce(
      `progress-bar-options:${componentId}`,
      '[signage] ProgressBar options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  return <ProgressBar interval={parsed.data.interval} layout={parsed.data.layout} />;
}
